import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { RefreshCw, Download, Sparkles, X, Clock } from 'lucide-react'
import { useRegisterSW } from 'virtual:pwa-register/react'

interface UpdatePromptProps {
  onUpdate?: () => void
}

export default function UpdatePrompt({ onUpdate }: UpdatePromptProps) {
  const [showBanner, setShowBanner] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [updateDismissed, setUpdateDismissed] = useState(false)

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      console.log('✅ SW Registered:', registration)

      // Check for updates every hour
      if (registration) {
        setInterval(() => {
          console.log('🔍 Checking for SW updates...')
          registration.update()
        }, 60 * 60 * 1000) // 1 hour
      }
    },
    onRegisterError(error) {
      console.error('❌ SW registration error:', error)
    },
  })

  useEffect(() => {
    if (needRefresh && !updateDismissed) {
      // Show banner immediately
      setShowBanner(true)

      // Show modal after 3 seconds if user hasn't interacted
      const timer = setTimeout(() => {
        if (!updateDismissed) {
          setShowModal(true)
        }
      }, 3000)

      // Send push notification about update
      sendUpdateNotification()

      return () => clearTimeout(timer)
    }
  }, [needRefresh, updateDismissed])

  const sendUpdateNotification = async () => {
    if ('serviceWorker' in navigator && 'Notification' in window) {
      const permission = await Notification.permission

      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready

        const notificationOptions: any = {
          body: 'A new version of ordore Analytics is available. Tap to update now!',
          icon: '/android-chrome-192x192.png',
          badge: '/android-chrome-192x192.png',
          tag: 'app-update',
          requireInteraction: true,
          vibrate: [200, 100, 200],
          data: {
            type: 'app_update',
            url: '/',
          },
          actions: [
            {
              action: 'update',
              title: 'Update Now',
              icon: '/android-chrome-192x192.png',
            },
            {
              action: 'later',
              title: 'Later',
              icon: '/android-chrome-192x192.png',
            },
          ],
        }

        registration.showNotification('🎉 Update Available!', notificationOptions)
      }
    }
  }

  const handleUpdate = async () => {
    setIsUpdating(true)

    try {
      await updateServiceWorker(true)

      // Optional callback
      if (onUpdate) {
        onUpdate()
      }

      // Reload will happen automatically
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error) {
      console.error('Update failed:', error)
      setIsUpdating(false)
    }
  }

  const handleDismiss = () => {
    setUpdateDismissed(true)
    setShowBanner(false)
    setShowModal(false)
    setNeedRefresh(false)
  }

  const handleRemindLater = () => {
    setShowModal(false)
    setShowBanner(true)

    // Show modal again after 1 hour
    setTimeout(() => {
      if (!updateDismissed) {
        setShowModal(true)
      }
    }, 60 * 60 * 1000)
  }

  if (!needRefresh || updateDismissed) {
    return null
  }

  return (
    <>
      {/* Floating Banner */}
      {showBanner && !showModal && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50 animate-in slide-in-from-bottom-5">
          <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950/20 shadow-lg">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-blue-900 dark:text-blue-100">
                  Update Available
                </div>
                <AlertDescription className="text-blue-800 dark:text-blue-200 text-sm mt-1">
                  A new version is ready to install
                </AlertDescription>
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    onClick={handleUpdate}
                    disabled={isUpdating}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isUpdating ? (
                      <>
                        <RefreshCw className="mr-2 h-3 w-3 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-3 w-3" />
                        Update Now
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowModal(true)}
                    className="text-xs"
                  >
                    Details
                  </Button>
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={handleDismiss}
                className="h-6 w-6 flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {/* Update Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
                <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <DialogTitle className="text-xl">New Update Available!</DialogTitle>
            </div>
            <DialogDescription className="text-base pt-2">
              We've improved ordore Analytics with new features, bug fixes, and performance enhancements.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  What's New
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                  <p>Improved performance and faster load times</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                  <p>Enhanced security and bug fixes</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                  <p>New features and UI improvements</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                  <p>Better offline support</p>
                </div>
              </CardContent>
            </Card>

            <Alert>
              <Clock className="h-4 w-4" />
              <AlertDescription className="text-sm">
                The update takes less than 5 seconds and won't interrupt your work. Your data is safe and will remain intact.
              </AlertDescription>
            </Alert>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isUpdating ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Update Now
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleRemindLater}
              disabled={isUpdating}
              className="flex-1"
            >
              <Clock className="mr-2 h-4 w-4" />
              Remind Me Later
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            You can always update later from the app settings
          </p>
        </DialogContent>
      </Dialog>

      {/* Offline Ready Notice */}
      {offlineReady && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-40 animate-in slide-in-from-bottom-5">
          <Alert className="border-green-500 bg-green-50 dark:bg-green-950/20">
            <div className="flex items-start gap-3">
              <Download className="h-5 w-5 text-green-600 dark:text-green-400" />
              <div className="flex-1">
                <div className="font-semibold text-green-900 dark:text-green-100">
                  App Ready for Offline Use
                </div>
                <AlertDescription className="text-green-800 dark:text-green-200 text-sm mt-1">
                  You can now use the app without internet connection
                </AlertDescription>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setOfflineReady(false)}
                className="h-6 w-6"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </Alert>
        </div>
      )}
    </>
  )
}
